import React, { useState, useEffect } from 'react';
import { db } from '../firebase/firestore';
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  limit,
  startAfter
} from "firebase/firestore";
import { sendPrivateMessage } from '../utils/sendPrivateMessage';

const UserManagementPanel = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [user, setUser] = useState(null);
  const [editUserData, setEditUserData] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState({});
  const [messageContent, setMessageContent] = useState('');
  const [messageCaption, setMessageCaption] = useState('');
  const [sendMessageStatus, setSendMessageStatus] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedUserForMessage, setSelectedUserForMessage] = useState(null);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [messageType, setMessageType] = useState('text');
  const [hasMore, setHasMore] = useState(true);
  
  // Новые состояния для popup
  const [isUserPopupOpen, setIsUserPopupOpen] = useState(false);
  const [popupUser, setPopupUser] = useState(null);
  const [popupEditUserData, setPopupEditUserData] = useState(null);
  const [isPopupEditing, setIsPopupEditing] = useState(false);

  // Очистка сообщений через 5 секунд
  useEffect(() => {
    let timer;
    if (successMessage || errorMessage) {
      timer = setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  // Вспомогательная функция для получения полных данных пользователя
  const getFullUserData = (userId) => {
    if (!userId) return null;
    
    // Сначала ищем в списке пользователей
    const userFromList = users.find(u => u.id === userId);
    if (userFromList) return userFromList;
    
    // Если не нашли и текущий пользователь имеет нужный ID, возвращаем его
    if (user && user.id === userId) return user;
    
    return null;
  };

  // Открытие popup для просмотра пользователя
  const openUserPopup = (userData, editing = false) => {
    const fullUserData = getFullUserData(userData.id) || userData;
    setPopupUser(fullUserData);
    setPopupEditUserData({ ...fullUserData });
    setIsPopupEditing(editing);
    setIsUserPopupOpen(true);
  };

  // Закрытие popup
  const closeUserPopup = () => {
    setIsUserPopupOpen(false);
    setPopupUser(null);
    setPopupEditUserData(null);
    setIsPopupEditing(false);
  };

  // Обработчик открытия модалки для сообщения
  const openMessageModal = (user) => {
    setSelectedUserForMessage(user);
    setIsMessageModalOpen(true);
    // Очистить предыдущее сообщение
    setMessageContent('');
    setMessageCaption('');
    setMessageType('text');
    setSendMessageStatus('');
  };

  // Обработчик закрытия модалки
  const closeMessageModal = () => {
    setIsMessageModalOpen(false);
    setSelectedUserForMessage(null);
    setSendMessageStatus('');
    setSendingMessage(false);
  };

  // Отправка сообщения
  const handleSendMessage = async () => {
    if (!selectedUserForMessage) {
      setSendMessageStatus('❌ Выберите пользователя');
      return;
    }
    
    if (!messageContent.trim()) {
      setSendMessageStatus('❌ Введите содержимое сообщения');
      return;
    }

    // Валидация URL для фото/видео
    if ((messageType === 'photo' || messageType === 'video') && !isValidUrl(messageContent)) {
      setSendMessageStatus('❌ Введите корректный URL');
      return;
    }

    setSendMessageStatus('⏳ Отправка...');
    setSendingMessage(true);
    
    try {
      console.log('Отправка сообщения пользователю:', selectedUserForMessage.id);
      console.log('Сообщение:', {
        type: messageType,
        content: messageContent,
        caption: messageCaption || undefined,
      });
      
      const result = await sendPrivateMessage(selectedUserForMessage.id, {
        type: messageType,
        content: messageContent,
        caption: messageCaption || undefined,
      });
      
      console.log('Результат отправки:', result);
      
      setSendMessageStatus('✅ Сообщение успешно отправлено');
      
      // Автоматическое закрытие модалки через 3 секунды
      setTimeout(() => {
        closeMessageModal();
      }, 3000);
      
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
      const errorMsg = error.message || 'неизвестная ошибка';
      setSendMessageStatus(`❌ Ошибка отправки: ${errorMsg}`);
      
      // Предложение проверить сервер
      if (errorMsg.includes('502') || errorMsg.includes('Gateway')) {
        setSendMessageStatus(prev => `${prev}. Проверьте, запущен ли API сервер.`);
      }
    } finally {
      setSendingMessage(false);
    }
  };

  // Проверка URL
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setErrorMessage('Введите имя пользователя или ID');
      return;
    }

    setLoading(true);
    setUser(null);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      let userFound = false;
      const cleanSearchTerm = searchTerm.trim();

      // Поиск по Telegram ID (числовой)
      const userDoc = await getDoc(doc(db, "telegramUsers", cleanSearchTerm));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userWithId = { id: userDoc.id, ...userData };
        // Открываем popup вместо установки состояния
        openUserPopup(userWithId, false);
        userFound = true;
        setSuccessMessage('✅ Пользователь найден по ID');
      }

      // Поиск по username (если не нашли по ID)
      if (!userFound) {
        const username = cleanSearchTerm.startsWith('@') 
          ? cleanSearchTerm.substring(1) 
          : cleanSearchTerm;
        
        // Ищем пользователей с таким username
        const usersRef = collection(db, "telegramUsers");
        const querySnapshot = await getDocs(usersRef);
        
        const foundUser = querySnapshot.docs.find(doc => {
          const data = doc.data();
          return data.username && data.username.toLowerCase() === username.toLowerCase();
        });
        
        if (foundUser) {
          const userData = foundUser.data();
          const userWithId = { id: foundUser.id, ...userData };
          // Открываем popup вместо установки состояния
          openUserPopup(userWithId, false);
          userFound = true;
          setSuccessMessage('✅ Пользователь найден по username');
        }
      }

      if (!userFound) {
        setErrorMessage('❌ Пользователь не найден');
      }

    } catch (error) {
      console.error("Ошибка поиска пользователя:", error);
      setErrorMessage('❌ Ошибка при поиске пользователя: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPopupEditUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Обработчик изменения даты рождения
  const handleBirthDateChange = (e) => {
    const { value } = e.target;
    setPopupEditUserData(prev => ({
      ...prev,
      birthDate: value
    }));
  };

  const handleUpdateUser = async () => {
    if (!popupUser) return;
    
    try {
      const userDoc = doc(db, "telegramUsers", popupUser.id);
      
      // Подготовка данных для обновления
      const updateData = { ...popupEditUserData };
      delete updateData.id; // Удаляем id если он есть
      delete updateData.createdAt; // Удаляем системные поля
      delete updateData.updatedAt;
      
      // Добавляем timestamp обновления
      updateData.updatedAt = new Date().toISOString();
      
      await updateDoc(userDoc, updateData);
      setSuccessMessage('✅ Пользователь успешно обновлен!');
      
      // Обновляем данные в popup
      setPopupUser(prev => {
        if (!prev) return prev;
        return {
          ...prev, // Сохраняем все предыдущие поля
          ...updateData, // Добавляем обновленные поля
          id: popupUser.id // Гарантируем, что id останется
        };
      });
      
      // Обновляем в списке пользователей
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === popupUser.id ? { 
            ...u, // Сохраняем все поля пользователя из списка
            ...updateData // Добавляем обновленные данные
          } : u
        )
      );
      
      setIsPopupEditing(false);
      
    } catch (error) {
      console.error("Ошибка обновления пользователя:", error);
      setErrorMessage('❌ Ошибка при обновлении пользователя: ' + error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      return;
    }

    try {
      const userDoc = doc(db, "telegramUsers", userId);
      await deleteDoc(userDoc);
      setUsers(users.filter(user => user.id !== userId));
      
      // Если удаляем пользователя, который открыт в popup
      if (popupUser && popupUser.id === userId) {
        closeUserPopup();
      }
      
      setSuccessMessage('✅ Пользователь успешно удален!');
    } catch (error) {
      console.error("Ошибка удаления пользователя:", error);
      setErrorMessage('❌ Ошибка при удалении пользователя: ' + error.message);
    }
  };

  const fetchUsers = async (loadMore = false) => {
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    try {
      const usersRef = collection(db, "telegramUsers");
      
      let querySnapshot;
      
      if (loadMore && lastVisible) {
        const q = query(usersRef, startAfter(lastVisible), limit(50));
        querySnapshot = await getDocs(q);
      } else {
        const q = query(usersRef, limit(50));
        querySnapshot = await getDocs(q);
      }
      
      const newLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastVisible(newLastVisible);
      
      const fetchedUsers = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          username: data.username || null,
          fullName: data.fullName || null,
          refBonus: data.refBonus ?? 0,
          email: data.email || null,
          phone: data.phone || null,
          birthDate: data.birthDate || null,
          savedAddress: data.savedAddress || null,
          joinedAt: data.joinedAt || null,
          referrerId: data.referrerId || null,
          ...data
        };
      });

      console.log(`Загружено ${fetchedUsers.length} пользователей`);
      
      if (loadMore) {
        setUsers(prev => [...prev, ...fetchedUsers]);
      } else {
        setUsers(fetchedUsers);
      }
      
      setHasMore(querySnapshot.docs.length === 50);
      
    } catch (error) {
      console.error("Ошибка загрузки пользователей:", error);
      setErrorMessage('❌ Ошибка при загрузке пользователей: ' + error.message);
    } finally {
      if (loadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line 
  }, []);

  const toggleDropdown = (userId) => {
    setDropdownVisible(prevState => ({
      ...prevState,
      [userId]: !prevState[userId]
    }));
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return 'не указана';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU');
    } catch {
      return dateString;
    }
  };

  // Получение адреса пользователя
  const getUserAddress = (userData) => {
    if (userData.savedAddress && userData.savedAddress.street) {
      return userData.savedAddress.street;
    }
    return 'не указан';
  };

  return (
    <div className="w-full flex flex-col space-y-4 h-[100vh] scroller pt-4 overflow-y-auto pb-[150px]">
      <div className='w-full sm:w-[50%] flex flex-col gap-3'>
        <div className="flex gap-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Введите Telegram ID или username"
            className="flex-grow bg-[#4b4b4b] placeholder:text-[#b9b9b9] text-[#e0e0e0] placeholder:text-[12px] text-[13px] placeholder:font-light h-[55px] border-none outline-none rounded-[10px] flex items-center px-6"
          />
          <button
            onClick={handleSearch}
            disabled={loading}
            className="bg-[#f5bb5f] font-semibold text-[15px] rounded-[6px] w-[120px] h-[55px] text-[#000] disabled:opacity-50"
          >
            {loading ? 'Поиск...' : 'Поиск'}
          </button>
        </div>

        {errorMessage && (
          <div className="bg-red-900/30 border border-red-700 text-red-300 p-3 rounded">
            {errorMessage}
          </div>
        )}
        
        {successMessage && (
          <div className="bg-green-900/30 border border-green-700 text-green-300 p-3 rounded">
            {successMessage}
          </div>
        )}
      </div>

      <div className="w-full sm:w-[50%] flex flex-col space-y-3">
        <div className="flex justify-between items-center">
          <h2 className="text-[20px] font-semibold">
            Список пользователей {users.length > 0 ? `(${users.length})` : ''}
          </h2>
          <button
            onClick={() => fetchUsers()}
            className="bg-gray-700 rounded-[6px] text-white px-4 py-2 text-sm"
          >
            Обновить
          </button>
        </div>
        
        {loading ? (
          <div className="text-center p-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            <p className="mt-2 text-gray-400">Загрузка пользователей...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center p-8 text-gray-400">
            Нет пользователей. Нажмите "Обновить" для загрузки.
          </div>
        ) : (
          <>
            {users.map((userItem, index) => (
              <div key={userItem.id} className="bg-[#2a2a2a] p-4 rounded-[10px] text-[13px] relative border border-gray-700 hover:border-gray-600 transition-colors">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className='w-6 h-6 flex-shrink-0 flex justify-center items-center rounded-full bg-gray-700 text-xs font-bold'>
                      {index + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-white truncate">
                        {userItem.username ? `@${userItem.username}` : userItem.fullName || 'Без username'}
                      </p>
                      <div className="text-xs text-gray-400 truncate">
                        <p>ID: {userItem.id}</p>
                        {userItem.phone && <p>📱 {userItem.phone}</p>}
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => toggleDropdown(userItem.id)}
                    className="text-gray-400 hover:text-white flex-shrink-0 ml-2"
                  >
                    ⋮
                  </button>
                </div>

                <div className="mb-3 space-y-1">
                  {userItem.fullName && (
                    <p className="text-sm text-gray-300 truncate">
                      👤 {userItem.fullName}
                    </p>
                  )}
                  {userItem.email && (
                    <p className="text-xs text-gray-400 truncate">
                      📧 {userItem.email}
                    </p>
                  )}
                  {userItem.birthDate && (
                    <p className="text-xs text-gray-400">
                      🎂 {formatDate(userItem.birthDate)}
                    </p>
                  )}
                </div>
                
                <p className="text-xs text-green-400">
                  💰 Реф. бонус: {userItem.refBonus} ₽
                </p>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => openMessageModal(userItem)}
                    className="bg-purple-600 hover:bg-purple-700 rounded-[6px] text-white px-3 py-1.5 text-sm flex-1 transition-colors"
                  >
                    Сообщение
                  </button>
                  <button
                    onClick={() => openUserPopup(userItem, false)}
                    className="bg-blue-600 hover:bg-blue-700 rounded-[6px] text-white px-3 py-1.5 text-sm transition-colors"
                  >
                    Просмотр
                  </button>
                </div>

                {dropdownVisible[userItem.id] && (
                  <div className="absolute z-10 top-10 right-2 bg-[#1a1a1a] border border-gray-700 rounded-md shadow-lg w-40">
                    <button
                      onClick={() => {
                        openUserPopup(userItem, true);
                        setDropdownVisible({});
                      }}
                      className="block w-full text-left text-white px-4 py-2 hover:bg-gray-800 text-sm"
                    >
                      Редактировать
                    </button>

                    <button
                      onClick={() => {
                        handleDeleteUser(userItem.id);
                        setDropdownVisible({});
                      }}
                      className="block w-full text-left px-4 py-2 hover:bg-red-900 text-sm text-red-400"
                    >
                      Удалить
                    </button>
                  </div>
                )}
              </div>
            ))}
            
            {hasMore && (
              <button
                onClick={() => fetchUsers(true)}
                disabled={loadingMore}
                className="bg-[#f5bb5f] hover:bg-[#e0aa50] font-semibold text-[15px] rounded-[6px] w-full h-fit px-4 py-3 text-[#000] disabled:opacity-50 flex justify-center items-center transition-colors"
              >
                {loadingMore ? (
                  <>
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></span>
                    Загрузка...
                  </>
                ) : 'Загрузить еще'}
              </button>
            )}
          </>
        )}
      </div>

      {/* Popup для просмотра и редактирования пользователя */}
      {isUserPopupOpen && popupUser && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg p-6 text-white w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-semibold text-xl">
                {isPopupEditing ? 'Редактирование пользователя' : 'Информация о пользователе'}
              </h3>
              <button
                onClick={closeUserPopup}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            
            {isPopupEditing ? (
              <>
                <div className="space-y-4 mb-6">
                  {/* Поле для ФИО */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[14px] text-white pl-1 pb-[2px] font-medium">ФИО *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={popupEditUserData.fullName || ''}
                      onChange={handleInputChange}
                      className="bg-[#4b4b4b] placeholder:text-[#b9b9b9] text-[#e0e0e0] text-[14px] h-[50px] px-4 rounded-[10px] border-none outline-none"
                      placeholder="Иванов Иван Иванович"
                      required
                    />
                  </div>

                  {/* Поле для телефона */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[14px] text-white pl-1 pb-[2px] font-medium">Телефон *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={popupEditUserData.phone || ''}
                      onChange={handleInputChange}
                      className="bg-[#4b4b4b] placeholder:text-[#b9b9b9] text-[#e0e0e0] text-[14px] h-[50px] px-4 rounded-[10px] border-none outline-none"
                      placeholder="+7 (999) 999-99-99"
                      required
                    />
                  </div>

                  {/* Поле для реферального бонуса */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[14px] text-white pl-1 pb-[2px] font-medium">
                      Реферальный бонус
                    </label>
                    <input
                      type="number"
                      name="refBonus"
                      value={popupEditUserData.refBonus ?? 0}
                      onChange={(e) =>
                        setPopupEditUserData(prev => ({
                          ...prev,
                          refBonus: Number(e.target.value)
                        }))
                      }
                      className="bg-[#4b4b4b] text-[#e0e0e0] text-[14px] h-[50px] px-4 rounded-[10px] border-none outline-none"
                      placeholder="0"
                      min="0"
                    />
                  </div>

                  {/* Поле для email */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[14px] text-white pl-1 pb-[2px] font-medium">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={popupEditUserData.email || ''}
                      onChange={handleInputChange}
                      className="bg-[#4b4b4b] placeholder:text-[#b9b9b9] text-[#e0e0e0] text-[14px] h-[50px] px-4 rounded-[10px] border-none outline-none"
                      placeholder="example@mail.com"
                    />
                  </div>

                  {/* Поле для дня рождения */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[14px] text-white pl-1 pb-[2px] font-medium">День рождения</label>
                    <input
                      type="date"
                      value={popupEditUserData.birthDate || ''}
                      onChange={handleBirthDateChange}
                      className="bg-[#4b4b4b] placeholder:text-[#b9b9b9] text-[#e0e0e0] text-[14px] h-[50px] px-4 rounded-[10px] border-none outline-none"
                    />
                  </div>

                  {/* Поле для адреса */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[14px] text-white pl-1 pb-[2px] font-medium">Адрес</label>
                    <textarea
                      name="savedAddress"
                      value={popupEditUserData.savedAddress?.street || ''}
                      onChange={(e) => setPopupEditUserData(prev => ({
                        ...prev,
                        savedAddress: {
                          ...prev.savedAddress,
                          street: e.target.value
                        }
                      }))}
                      className="bg-[#4b4b4b] placeholder:text-[#b9b9b9] text-[#e0e0e0] text-[14px] h-[50px] px-4 rounded-[10px] border-none outline-none min-h-[60px]"
                      placeholder="ул. Пушкина, д. 10, кв. 5"
                      rows="2"
                    />
                  </div>

                  {/* Поле для инструкций по доставке */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[14px] text-white pl-1 pb-[2px] font-medium">Инструкции по доставке</label>
                    <textarea
                      name="deliveryInstructions"
                      value={popupEditUserData.savedAddress?.deliveryInstructions || ''}
                      onChange={(e) => setPopupEditUserData(prev => ({
                        ...prev,
                        savedAddress: {
                          ...prev.savedAddress,
                          deliveryInstructions: e.target.value
                        }
                      }))}
                      className="bg-[#4b4b4b] placeholder:text-[#b9b9b9] text-[#e0e0e0] text-[14px] px-4 rounded-[10px] border-none outline-none min-h-[80px]"
                      placeholder="Код домофона, подъезд, этаж"
                      rows="3"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4 border-t border-gray-700">
                  <button
                    onClick={handleUpdateUser}
                    className="flex-1 bg-green-600 hover:bg-green-700 rounded-[8px] text-white px-6 py-3 font-semibold transition-colors"
                  >
                    Сохранить изменения
                  </button>
                  <button
                    onClick={() => setIsPopupEditing(false)}
                    className="bg-gray-600 hover:bg-gray-700 rounded-[8px] text-white px-6 py-3 font-semibold transition-colors"
                  >
                    Отмена редактирования
                  </button>
                  <button
                    onClick={closeUserPopup}
                    className="bg-gray-800 hover:bg-gray-700 rounded-[8px] text-white px-6 py-3 font-semibold transition-colors"
                  >
                    Закрыть
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-300 mb-2">Основная информация</h4>
                      <div className="space-y-2">
                        <p><strong>Telegram ID:</strong> {popupUser.id}</p>
                        <p><strong>Username:</strong> {popupUser.username ? `@${popupUser.username}` : 'не указан'}</p>
                        <p><strong>ФИО:</strong> {popupUser.fullName || 'не указано'}</p>
                        <p><strong>Телефон:</strong> {popupUser.phone || 'не указан'}</p>
                        <p><strong>Email:</strong> {popupUser.email || 'не указан'}</p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-300 mb-2">Дополнительная информация</h4>
                      <div className="space-y-2">
                        <p><strong>День рождения:</strong> {formatDate(popupUser.birthDate)}</p>
                        <p><strong>Адрес:</strong> {getUserAddress(popupUser)}</p>
                        {popupUser.savedAddress?.deliveryInstructions && (
                          <p><strong>Инструкции по доставке:</strong> {popupUser.savedAddress.deliveryInstructions}</p>
                        )}
                        <p><strong>Дата регистрации:</strong> {formatDate(popupUser.joinedAt)}</p>
                        <p>
                          <strong>Реферальный бонус:</strong>{' '}
                          {typeof popupUser.refBonus === 'number' ? `${popupUser.refBonus} ₽` : '0 ₽'}
                        </p>
                        {popupUser.referrerId && (
                          <p><strong>Пригласил:</strong> {popupUser.referrerId}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4 border-t border-gray-700">
                  <button
                    onClick={() => setIsPopupEditing(true)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 rounded-[8px] text-white px-6 py-3 font-semibold transition-colors"
                  >
                    Редактировать
                  </button>
                  <button
                    onClick={() => openMessageModal(popupUser)}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 rounded-[8px] text-white px-6 py-3 font-semibold transition-colors"
                  >
                    Отправить сообщение
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
                        handleDeleteUser(popupUser.id);
                      }
                    }}
                    className="flex-1 bg-red-600 hover:bg-red-700 rounded-[8px] text-white px-6 py-3 font-semibold transition-colors"
                  >
                    Удалить
                  </button>
                  <button
                    onClick={closeUserPopup}
                    className="bg-gray-800 hover:bg-gray-700 rounded-[8px] text-white px-6 py-3 font-semibold transition-colors"
                  >
                    Закрыть
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Модальное окно отправки сообщения */}
      {isMessageModalOpen && selectedUserForMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg p-6 text-white w-full max-w-md border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg">
                Отправить сообщение
              </h3>
              <button
                onClick={closeMessageModal}
                className="text-gray-400 hover:text-white text-xl"
                disabled={sendingMessage}
              >
                ×
              </button>
            </div>
            
            <div className="mb-4 p-3 bg-gray-800 rounded">
              <p className="text-sm text-gray-300">Пользователь:</p>
              <p className="font-semibold">
                {selectedUserForMessage.username ? `@${selectedUserForMessage.username}` : selectedUserForMessage.fullName || selectedUserForMessage.id}
              </p>
              <p className="text-sm text-gray-400">Telegram ID: {selectedUserForMessage.id}</p>
              {selectedUserForMessage.phone && (
                <p className="text-sm text-gray-400">Телефон: {selectedUserForMessage.phone}</p>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Тип сообщения</label>
                <select
                  value={messageType}
                  onChange={(e) => setMessageType(e.target.value)}
                  className="w-full p-2 rounded bg-gray-800 border border-gray-700 text-white"
                  disabled={sendingMessage}
                >
                  <option value="text">📝 Текст</option>
                  <option value="photo">🖼 Фото (по URL)</option>
                  <option value="video">🎬 Видео (по URL)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {messageType === 'text' ? 'Текст сообщения' : 'URL файла'}
                </label>
                {messageType === 'text' ? (
                  <textarea
                    placeholder="Введите текст сообщения..."
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    className="w-full p-2 rounded bg-gray-800 border border-gray-700 text-white min-h-[100px]"
                    disabled={sendingMessage}
                    rows={4}
                  />
                ) : (
                  <input
                    type="text"
                    placeholder={`Введите URL ${messageType === 'photo' ? 'фото' : 'видео'}...`}
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    className="w-full p-2 rounded bg-gray-800 border border-gray-700 text-white"
                    disabled={sendingMessage}
                  />
                )}
              </div>

              {(messageType === 'photo' || messageType === 'video') && (
                <div>
                  <label className="block text-sm font-medium mb-1">Подпись (необязательно)</label>
                  <input
                    type="text"
                    placeholder="Введите подпись..."
                    value={messageCaption}
                    onChange={(e) => setMessageCaption(e.target.value)}
                    className="w-full p-2 rounded bg-gray-800 border border-gray-700 text-white"
                    disabled={sendingMessage}
                  />
                </div>
              )}

              {sendMessageStatus && (
                <div className={`p-3 rounded text-sm ${
                  sendMessageStatus.includes('✅') ? 'bg-green-900/30 text-green-300' :
                  sendMessageStatus.includes('❌') ? 'bg-red-900/30 text-red-300' :
                  'bg-blue-900/30 text-blue-300'
                }`}>
                  {sendMessageStatus}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !messageContent.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-3 rounded font-semibold text-white transition-colors"
                >
                  {sendingMessage ? (
                    <>
                      <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                      Отправка...
                    </>
                  ) : 'Отправить'}
                </button>
                <button
                  onClick={closeMessageModal}
                  disabled={sendingMessage}
                  className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 px-4 py-3 rounded font-semibold transition-colors"
                >
                  Отмена
                </button>
              </div>

              <div className="text-xs text-gray-400 mt-4">
                <p>📌 Сообщение будет отправлено через Telegram Bot API</p>
                <p>📌 Для фото и видео укажите публичный URL</p>
                <p>📌 Пример URL: https://example.com/image.jpg</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPanel;